import React, { useMemo, useState } from "react";
import { WebsiteBuilderPanelContent } from "pages/secure/facility/websiteBuilder/editor/shell/panel/content/WebsiteBuilderPanelContent";
import Input from "components/form/input/Input";
import { CustomColorPickerPopover } from "components/colorpicker";
import { useWebsiteBuilderDebouncedUpdate, WebsiteBuilderDebouncedUpdateTime } from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import { IWebsiteBuilderLinkSettings, TWebsiteBuilderLinkDestination, TWebsiteBuilderLinkDestinationType } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderLink";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { websiteBuilderUpdateElementSettings } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { cloneDeep } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink } from "@fortawesome/pro-regular-svg-icons";
import Checkbox from "components/form/checkbox/Checkbox";
import { t } from "i18next";
import { IWebsiteBuilderSelectOption, renderWebsiteBuilderSelectOptions } from "../../../../../helpers/renderWebsiteBuilderSelectOptions";
import { Select } from "components/select/index";
import { WEBSITE_BUILDER_PORTAL } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";
import { WebsiteBuilderHoverSettings } from "../../../../../helpers/WebsiteBuilderHoverSettings";

interface ILinkSettingsProps {
  id: string;
  settings: IWebsiteBuilderLinkSettings;
  closeSettings: () => void;
}

export function LinkSettings(props: ILinkSettingsProps) {
  const { id, settings, closeSettings } = props;

  const dispatch = useAppDispatch();

  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const [updatedSettings, setUpdatedSettings] = useState<IWebsiteBuilderLinkSettings>(() => cloneDeep(settings));

  function dispatchSettings(prevSettings: IWebsiteBuilderLinkSettings, nextSettings: IWebsiteBuilderLinkSettings) {
    dispatch(
      websiteBuilderUpdateElementSettings(activePageId, deviceView, id, {
        id,
        prevElementSettings: {
          type: "link",
          settings: cloneDeep(prevSettings),
        },
        nextElementSettings: {
          type: "link",
          settings: cloneDeep(nextSettings),
        },
      }),
    );
  }

  const { handleUpdateState, handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: settings,
    updatedState: updatedSettings,
    setUpdatedState: setUpdatedSettings,
    dispatchState: dispatchSettings,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.MEDIUM,
  });

  return (
    <WebsiteBuilderPanelContent
      title={t("Link settings")}
      closePanel={closeSettings}
      sections={[
        <WebsiteBuilderLinkSettingsFields
          key="link-settings"
          settings={updatedSettings}
          onChange={nextSettings => handleUpdateState(() => nextSettings)}
          onDebouncedChange={nextSettings => handleDebouncedUpdateState(() => nextSettings)}
        />,
      ]}
    />
  );
}

interface IWebsiteBuilderLinkSettingsFieldsProps {
  settings: IWebsiteBuilderLinkSettings;
  onChange: (settings: IWebsiteBuilderLinkSettings) => void;
  onDebouncedChange: (settings: IWebsiteBuilderLinkSettings) => void;
  showHoverSettings?: boolean;
}

export function WebsiteBuilderLinkSettingsFields(props: IWebsiteBuilderLinkSettingsFieldsProps) {
  const { settings, onChange, onDebouncedChange, showHoverSettings = true } = props;

  const destinationTypeOptions: IWebsiteBuilderSelectOption<TWebsiteBuilderLinkDestinationType>[] = useMemo(() => {
    return [
      {
        label: t("Url"),
        value: "url",
      },
      {
        label: t("Email"),
        value: "email",
      },
    ];
  }, []);

  function renderAppearanceFields() {
    return (
      <>
        <Input
          label={t("Label")}
          value={settings.label}
          onChange={e =>
            onDebouncedChange({
              ...settings,
              label: e.target.value,
            })
          }
        />
        <Checkbox
          size="medium"
          label={t("Underline")}
          checked={settings.underline}
          onChange={e =>
            onChange({
              ...settings,
              underline: e.target.checked,
            })
          }
        />
        <CustomColorPickerPopover
          title={t("Color")}
          hexColor={settings.color}
          updateHexColor={color =>
            onDebouncedChange({
              ...settings,
              color,
            })
          }
          portalParentId={WEBSITE_BUILDER_PORTAL}
        />
      </>
    );
  }
  
  function renderDestinationFields() {
    const destinationTypeSelect = 
      <Select
        label={t("Destination Type")}
        defaultValue={settings.destination.type}
        onChange={(destinationType: TWebsiteBuilderLinkDestinationType) => {
          if (destinationType !== settings.destination.type) {
            const destination: TWebsiteBuilderLinkDestination = destinationType === "url" ? {
              type: "url",
              href: "",
              blankTarget: false,
            } : {
              type: "email",
              address: "",
              subject: "",
            };

            onChange({
              ...settings,
              destination,
            });
          }
        }}
        usePortal
        portalId={WEBSITE_BUILDER_PORTAL}
      >
        {renderWebsiteBuilderSelectOptions(destinationTypeOptions)}
      </Select>;

    if (settings.destination.type === "url") {
      const destination = settings.destination;

      return (
        <>
          {destinationTypeSelect}
          <Input
            label={t("Link")}
            value={settings.destination.href}
            prefix={<FontAwesomeIcon icon={faLink} />}
            onChange={e =>
              onDebouncedChange({
                ...settings,
                destination: {
                  ...destination,
                  href: e.target.value,
                },
              })
            }
          />
          <Checkbox
            size="medium"
            label={t("Open link in new tab")}
            checked={settings.destination.blankTarget}
            onChange={e =>
              onChange({
                ...settings,
                destination: {
                  ...destination,
                  blankTarget: e.target.checked,
                },
              })
            }
          />
        </>
      );
    } else if (settings.destination.type === "email") {
      const destination = settings.destination;

      return (
        <>
          {destinationTypeSelect}
          <Input
            label={t("Address")}
            value={settings.destination.address}
            prefix={<FontAwesomeIcon icon={faLink} />}
            onChange={e =>
              onDebouncedChange({
                ...settings,
                destination: {
                  ...destination,
                  address: e.target.value,
                },
              })
            }
          />
          <Input
            label={t("Subject")}
            value={settings.destination.subject}
            onChange={e =>
              onDebouncedChange({
                ...settings,
                destination: {
                  ...destination,
                  subject: e.target.value,
                },
              })
            }
          />
        </>
      );
    } else {
      return destinationTypeSelect;
    }
  }
  
  return (
    <div className="website-builder-link-settings-fields">
      {renderAppearanceFields()}
      {renderDestinationFields()}
      {showHoverSettings && (
        <WebsiteBuilderHoverSettings>
          <CustomColorPickerPopover
            title={t("Color")}
            hexColor={settings.hoverColor}
            updateHexColor={hoverColor =>
              onDebouncedChange({
                ...settings,
                hoverColor,
              })
            }
            portalParentId={WEBSITE_BUILDER_PORTAL}
          />
        </WebsiteBuilderHoverSettings>
      )}
    </div>
  );
}